<script setup lang="ts">
/**
 * SliderCaptcha — 滑块拼图验证弹层(自研,配合后端 /site/captcha/slider)
 * - open() 弹出并加载挑战;拖动滑块对齐缺口后松手自动校验
 * - 通过 → emit('success', { challenge_id, ticket }) 由父组件携带 ticket 发送验证码
 * - 失败 → 显示错误并自动刷新新挑战
 * 缺口垂直居中(与后端 targetY = (height - gap_size) / 2 一致),水平坐标仅服务端持有。
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import YfbIcon from '@/components/YfbIcon.vue'
import { apiGetSliderChallenge, apiVerifySlider, type SliderChallenge } from '@/api/auth'

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'success', payload: { challenge_id: string; ticket: string }): void
  (e: 'close'): void
}>()

const visible = ref(false)
const loading = ref(false)
const error = ref('')
const challenge = ref<SliderChallenge | null>(null)

// 拖动状态
const sliderPos = ref(0)
const dragging = ref(false)
let startClientX = 0
let startClientY = 0
let startTime = 0
let track: { x: number; y: number }[] = []

const maxX = computed(() => (challenge.value ? challenge.value.width - challenge.value.gap_size : 0))
const pieceTop = computed(() => {
  if (!challenge.value) return 0
  // 缺口 Y 由后端随机下发（gap_y）；旧响应缺省回退居中
  return typeof challenge.value.gap_y === 'number'
    ? Math.round(challenge.value.gap_y)
    : Math.round((challenge.value.height - challenge.value.gap_size) / 2)
})

async function open() {
  visible.value = true
  await loadChallenge()
}

async function loadChallenge() {
  loading.value = true
  error.value = ''
  sliderPos.value = 0
  track = []
  try {
    challenge.value = await apiGetSliderChallenge()
  } catch {
    challenge.value = null
    error.value = t('login.sliderFail')
  } finally {
    loading.value = false
  }
}

function onPointerDown(e: PointerEvent) {
  if (!challenge.value || loading.value || dragging.value) return
  dragging.value = true
  startClientX = e.clientX
  startClientY = e.clientY
  startTime = Date.now()
  track = [{ x: 0, y: 0 }]
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
  if (!dragging.value || !challenge.value) return
  const dx = e.clientX - startClientX
  sliderPos.value = Math.max(0, Math.min(maxX.value, dx))
  track.push({ x: sliderPos.value, y: Math.round((e.clientY - startClientY) * 10) / 10 })
}

async function onPointerUp() {
  if (!dragging.value || !challenge.value) return
  dragging.value = false
  const duration = Date.now() - startTime
  const x = sliderPos.value
  const snapshot = challenge.value
  try {
    const res = await apiVerifySlider(snapshot.challenge_id, x, duration, track)
    emit('success', { challenge_id: snapshot.challenge_id, ticket: res.ticket })
    visible.value = false
    challenge.value = null
  } catch {
    error.value = t('login.sliderFail')
    await loadChallenge()
  }
}

function close() {
  visible.value = false
  challenge.value = null
  emit('close')
}

defineExpose({ open })
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="slider-captcha" role="dialog" aria-modal="true" :aria-label="t('login.sliderTitle')">
      <div class="slider-captcha__mask" @click.self="close">
        <div class="slider-captcha__panel">
          <header class="slider-captcha__head">
            <span class="slider-captcha__title">{{ t('login.sliderTitle') }}</span>
            <button type="button" class="slider-captcha__close" :aria-label="t('common.close')" @click="close">×</button>
          </header>

          <div class="slider-captcha__body">
            <div v-if="loading" class="slider-captcha__state">{{ t('login.sliderLoading') }}</div>

            <div v-else-if="error && !challenge" class="slider-captcha__state slider-captcha__state--error">
              <p>{{ error }}</p>
              <button type="button" class="slider-captcha__retry" @click="loadChallenge">
                {{ t('login.sliderRefresh') }}
              </button>
            </div>

            <template v-else-if="challenge">
              <div
                class="slider-captcha__puzzle"
                :style="{ width: challenge.width + 'px', height: challenge.height + 'px' }"
              >
                <img
                  class="slider-captcha__bg"
                  :src="challenge.bg"
                  :width="challenge.width"
                  :height="challenge.height"
                  alt=""
                />
                <img
                  class="slider-captcha__piece"
                  :src="challenge.slider"
                  :width="challenge.gap_size"
                  :height="challenge.gap_size"
                  :style="{ left: sliderPos + 'px', top: pieceTop + 'px' }"
                  alt=""
                />
              </div>

              <div class="slider-captcha__track">
                <span class="slider-captcha__track-hint">{{ t('login.sliderHint') }}</span>
                <div
                  class="slider-captcha__knob"
                  :style="{ transform: `translateX(${sliderPos}px)` }"
                  @pointerdown="onPointerDown"
                  @pointermove="onPointerMove"
                  @pointerup="onPointerUp"
                  @pointercancel="onPointerUp"
                >
                  <YfbIcon name="lucide:arrow-right" :size="18" />
                </div>
              </div>

              <p v-if="error" class="slider-captcha__fail" role="alert">{{ error }}</p>
            </template>
          </div>

          <footer class="slider-captcha__foot">
            <button type="button" class="slider-captcha__refresh" @click="loadChallenge">
              {{ t('login.sliderRefresh') }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.slider-captcha__mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: var(--yfb-sp-4);
  background: rgb(0 0 0 / 0.55);
  backdrop-filter: blur(2px);
}

.slider-captcha__panel {
  width: 100%;
  max-width: 21rem;
  padding: var(--yfb-sp-5);
  background: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: 0 0 24px var(--yfb-glow), 0 16px 48px var(--yfb-drop);
}

.slider-captcha__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--yfb-sp-4);
}

.slider-captcha__title {
  font-size: var(--yfb-fs-base);
  font-weight: 700;
  color: var(--yfb-ink);
}

.slider-captcha__close {
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  font-size: var(--yfb-fs-lg);
  line-height: 1;
  color: var(--yfb-ink-soft);
  background: transparent;
  border: none;
  cursor: pointer;
}

.slider-captcha__close:hover {
  color: var(--yfb-ink);
}

.slider-captcha__body {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
}

.slider-captcha__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-8) 0;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
}

.slider-captcha__state--error {
  color: var(--yfb-signal-danger);
}

.slider-captcha__puzzle {
  position: relative;
  max-width: 100%;
  overflow: hidden;
  border-radius: var(--yfb-radius-sm);
  border: 1px solid var(--yfb-rule);
}

.slider-captcha__bg {
  display: block;
  max-width: 100%;
  height: auto;
}

.slider-captcha__piece {
  position: absolute;
  max-width: none;
  user-select: none;
  pointer-events: none;
  filter: drop-shadow(0 0 6px rgb(0 0 0 / 0.5));
}

.slider-captcha__track {
  position: relative;
  height: 2.5rem;
  border-radius: var(--yfb-radius-sm);
  background: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  overflow: hidden;
}

.slider-captcha__track-hint {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
  user-select: none;
  pointer-events: none;
}

.slider-captcha__knob {
  position: absolute;
  top: 0;
  left: 0;
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 100%;
  color: var(--yfb-on-accent);
  background-image: var(--yfb-grad-cta);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  cursor: grab;
  touch-action: none;
  transition: filter var(--yfb-anim-fast);
}

.slider-captcha__knob:active {
  cursor: grabbing;
  filter: brightness(1.15);
}

.slider-captcha__fail {
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-signal-danger);
}

.slider-captcha__foot {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--yfb-sp-3);
}

.slider-captcha__refresh,
.slider-captcha__retry {
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  color: var(--yfb-accent);
  background: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
}

.slider-captcha__refresh:hover,
.slider-captcha__retry:hover {
  filter: brightness(1.2);
}
</style>
